/**
 * AutomationCommandHandler — AI chat command handler for automation rules.
 *
 * Parses natural language commands to create, modify, list, and remove
 * automation rules on PM components (Kanban, Gantt, Calendar).
 */

import { Component, ComponentType } from '@/types';
import { BaseCommandHandler, CommandResult, PropertyChange } from '../BaseCommandHandler';
import type { AutomationRule, AutomationAction, AutomationCondition, AutomationTrigger } from '@/types/pm-types';
import { instantiateTemplate, AUTOMATION_RULE_TEMPLATES } from '@/data/automationRuleTemplates';

const PM_TYPES = [
  ComponentType.KANBAN_BOARD,
  ComponentType.GANTT_CHART,
  ComponentType.CALENDAR,
  ComponentType.QUESTIONNAIRE,
];

const AUTOMATION_KEYWORDS = [
  'automat', 'rule', 'auto-set', 'auto-archive', 'auto-complete',
  'overdue', 'stale', 'automation', 'when.*then',
  'on add', 'on move', 'on change', 'trigger',
  'show.*automation', 'list.*automation', 'list.*rule',
  'disable.*rule', 'enable.*rule', 'turn off.*rule', 'turn on.*rule',
  'remove.*rule', 'delete.*rule',
];

export class AutomationCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Create rule: "add automation when cards move to Done set completion date"
    // Supports multi-word column names with quotes ("In Progress") or single words (Done)
    this.commandPatterns.set(
      /(?:add|create|set up|configure)\s+(?:an?\s+)?(?:automation|rule)\s+(?:that\s+)?when\s+(?:cards?\s+)?(?:move|moved|drag|dragged)\s+to\s+(?:["']([^"']+)["']|([\w]+(?:\s+\w+)*))\s+(?:then\s+)?set\s+["']?(\w+)["']?\s+(?:to\s+)?["']?(.+?)["']?\s*$/i,
      (match) => this.createColumnMoveRule(match[1] || match[2], match[3], match[4]),
    );

    // "auto-set [field] to [value] for new cards/items"
    this.commandPatterns.set(
      /auto[- ]?set\s+["']?(\w+)["']?\s+(?:to\s+)?["']?(.+?)["']?\s+(?:for|on|when)\s+new\s+(?:cards?|items?|records?)/i,
      (match) => this.createItemAddRule(match[1], match[2]),
    );

    // "archive items unchanged for N weeks/days"
    this.commandPatterns.set(
      /(?:auto[- ]?)?archive\s+(?:items?|cards?|records?)\s+(?:unchanged|stale|untouched)\s+(?:for\s+)?(\d+)\s+(days?|weeks?)/i,
      (match) => {
        const days = match[2].startsWith('week') ? Number(match[1]) * 7 : Number(match[1]);
        return this.createStaleRule(days, 'archive');
      },
    );

    // "when all sub-tasks done mark parent done"
    this.commandPatterns.set(
      /when\s+(?:all\s+)?sub[- ]?tasks?\s+(?:are\s+)?(?:done|complete|finished)\s+(?:then\s+)?(?:mark|set)\s+(?:the\s+)?parent\s+(?:as\s+)?(?:done|complete)/i,
      () => this.applyTemplate('auto-complete-parent'),
    );

    // "add overdue tag when due date passes"
    this.commandPatterns.set(
      /(?:add|flag|set)\s+(?:an?\s+)?overdue\s+(?:tag|flag|marker)\s+when\s+(?:the\s+)?(?:due\s+)?date\s+(?:passes|reached|expires)/i,
      () => this.applyTemplate('auto-flag-overdue'),
    );

    // "turn off the [rule name] rule" / "disable the [rule name] rule"
    this.commandPatterns.set(
      /(?:turn off|disable|deactivate)\s+(?:the\s+)?["']?(.+?)["']?\s*(?:rule|automation)?\s*$/i,
      (match, component) => this.toggleRule(component, match[1], false),
    );

    // "turn on the [rule name] rule" / "enable the [rule name] rule"
    this.commandPatterns.set(
      /(?:turn on|enable|activate)\s+(?:the\s+)?["']?(.+?)["']?\s*(?:rule|automation)?\s*$/i,
      (match, component) => this.toggleRule(component, match[1], true),
    );

    // "show active automations" / "list rules"
    this.commandPatterns.set(
      /(?:show|list|display|what are)\s+(?:the\s+)?(?:active\s+)?(?:automation|rule)s?\s*$/i,
      (_match, component) => this.listRules(component),
    );

    // "remove/delete the [rule name] rule"
    this.commandPatterns.set(
      /(?:remove|delete)\s+(?:the\s+)?["']?(.+?)["']?\s*(?:rule|automation)?\s*$/i,
      (match, component) => this.removeRule(component, match[1]),
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (!PM_TYPES.includes(component.type as ComponentType)) return false;
    const lowerCmd = command.toLowerCase();
    return AUTOMATION_KEYWORDS.some((kw) => {
      if (kw.includes('.*')) {
        return new RegExp(kw, 'i').test(lowerCmd);
      }
      return lowerCmd.includes(kw);
    });
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Try pattern matching first
    const patternMatch = this.matchPatterns(command, component);
    if (patternMatch) {
      return {
        success: true,
        command: {
          type: 'property',
          changes: patternMatch,
          confidence: this.calculateConfidence(command, patternMatch),
          description: this.generateDescription(patternMatch, component),
        },
      };
    }

    // Try template name matching as fallback
    const lowerCmd = command.toLowerCase();
    for (const template of AUTOMATION_RULE_TEMPLATES) {
      const templateWords = template.name.toLowerCase().split(/\s+/);
      const matchCount = templateWords.filter((w) => lowerCmd.includes(w)).length;
      if (matchCount >= 2) {
        const result = this.applyTemplate(template.id);
        if (result) {
          return {
            success: true,
            command: {
              type: 'property',
              changes: result,
              confidence: 0.7,
              description: `Applied template: ${template.name}`,
            },
          };
        }
      }
    }

    return {
      success: false,
      error: 'Could not understand automation rule command',
      suggestions: await this.getExampleCommands(component.type as ComponentType),
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'add automation when cards move to Done set completedDate to @today',
      'auto-set priority to Medium for new cards',
      'archive items unchanged for 2 weeks',
      'when all sub-tasks done mark parent done',
      'add overdue tag when due date passes',
      'show active automations',
      'turn off the auto-archive rule',
      'remove the overdue rule',
    ];
  }

  async getSuggestions(_component: Component): Promise<string[]> {
    return this.getExampleCommands(_component.type as ComponentType);
  }

  // ──── Rule Creation Helpers ────

  private createColumnMoveRule(column: string, field: string, value: string): PropertyChange {
    const rule = this.buildRule(
      `Auto-set ${field} on move to ${column}`,
      { type: 'onColumnMove', config: { toColumn: column.toLowerCase() } },
      [],
      [{ type: 'setField', config: { field, value: value === 'today' || value === '@today' ? '@today' : value } }],
    );
    return { properties: { _automationRuleToAdd: rule } };
  }

  private createItemAddRule(field: string, value: string): PropertyChange {
    const rule = this.buildRule(
      `Auto-set ${field} for new items`,
      { type: 'onItemAdd' },
      [{ type: 'fieldEmpty', field }],
      [{ type: 'setField', config: { field, value } }],
    );
    return { properties: { _automationRuleToAdd: rule } };
  }

  private createStaleRule(days: number, action: 'archive' | 'moveToColumn'): PropertyChange {
    const rule = this.buildRule(
      `Auto-${action} items stale for ${days} days`,
      { type: 'onStale', config: { days, field: 'updatedAt' } },
      [{ type: 'fieldNotEquals', field: 'status', value: 'Done' }],
      action === 'archive'
        ? [{ type: 'archive', config: {} }]
        : [{ type: 'moveToColumn', config: { column: 'Stale' } }],
    );
    return { properties: { _automationRuleToAdd: rule } };
  }

  private applyTemplate(templateId: string): PropertyChange | null {
    const rule = instantiateTemplate(templateId);
    if (!rule) return null;
    return { properties: { _automationRuleToAdd: rule } };
  }

  private toggleRule(component: Component, namePart: string, enabled: boolean): PropertyChange {
    const rules = (component.properties?.automationRules as AutomationRule[] | undefined) ?? [];
    const matchedId = this.findRuleId(rules, namePart);
    const updated = matchedId
      ? rules.map((r) => (r.id === matchedId ? { ...r, enabled } : r))
      : rules; // No match — return unchanged
    return { properties: { automationRules: updated } };
  }

  private listRules(component: Component): PropertyChange {
    const rules = (component.properties?.automationRules as AutomationRule[] | undefined) ?? [];
    const enabled = rules.filter((r) => r.enabled);
    const description = enabled.length === 0
      ? 'No active automation rules configured.'
      : enabled.map((r) => `- ${r.name} (${r.trigger.type})`).join('\n');
    // Return a no-op change with description embedded
    return {
      properties: {
        _automationRuleListDescription: description,
      },
    };
  }

  private removeRule(component: Component, namePart: string): PropertyChange {
    const rules = (component.properties?.automationRules as AutomationRule[] | undefined) ?? [];
    const matchedId = this.findRuleId(rules, namePart);
    const filtered = matchedId
      ? rules.filter((r) => r.id !== matchedId)
      : rules; // No match — return unchanged
    return { properties: { automationRules: filtered } };
  }

  /** Find a rule by name: exact match first, then substring. Returns the rule ID or null. */
  private findRuleId(rules: AutomationRule[], namePart: string): string | null {
    const lowerName = namePart.toLowerCase().trim();
    // Exact name match (case-insensitive)
    const exact = rules.find((r) => r.name.toLowerCase() === lowerName);
    if (exact) return exact.id;
    // Substring match — only if exactly one rule matches
    const substringMatches = rules.filter((r) => r.name.toLowerCase().includes(lowerName));
    if (substringMatches.length === 1) return substringMatches[0].id;
    return null;
  }

  private buildRule(
    name: string,
    trigger: AutomationTrigger,
    conditions: AutomationCondition[],
    actions: AutomationAction[],
  ): AutomationRule {
    return {
      id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name,
      enabled: true,
      trigger,
      conditions,
      actions,
    };
  }
}

// Singleton instance
export const automationCommandHandler = new AutomationCommandHandler();
